import { Globe, Languages, BookOpen, FileText, Sparkles, Check, Badge, LineChart, BarChart, ChevronRight, Zap, Target, Clock, Glasses, Laptop } from "lucide-react";
import { FeaturePageLayout } from "@/components/layout/FeaturePageLayout";
import { BenefitCard } from "@/components/ui/benefit-card";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
const TranslationsAnalysis = () => {
  const {
    t,
    i18n
  } = useTranslation("platform");
  const translationBenefits = [{
    title: t("translationsAnalysis.features.accurateTranslation"),
    description: t("translationsAnalysis.features.accurateTranslationDesc"),
    icon: <Check className="h-5 w-5 text-primary" />
  }, {
    title: t("translationsAnalysis.features.dataAnalysis"),
    description: t("translationsAnalysis.features.dataAnalysisDesc"),
    icon: <BarChart className="h-5 w-5 text-primary" />
  }, {
    title: t("translationsAnalysis.features.technicalTerms"),
    description: t("translationsAnalysis.features.technicalTermsDesc"),
    icon: <BookOpen className="h-5 w-5 text-primary" />
  }, {
    title: t("translationsAnalysis.features.aiPowered"),
    description: t("translationsAnalysis.features.aiPoweredDesc"),
    icon: <Sparkles className="h-5 w-5 text-primary" />
  }];
  const languages = [{
    code: "en",
    name: "English"
  }, {
    code: "fr",
    name: "French"
  }, {
    code: "de",
    name: "German"
  }, {
    code: "es",
    name: "Spanish"
  }, {
    code: "it",
    name: "Italian"
  }, {
    code: "nl",
    name: "Dutch"
  }, {
    code: "pt",
    name: "Portuguese"
  }, {
    code: "sv",
    name: "Swedish"
  }, {
    code: "da",
    name: "Danish"
  }, {
    code: "fi",
    name: "Finnish"
  }, {
    code: "pl",
    name: "Polish"
  }, {
    code: "cs",
    name: "Czech"
  }, {
    code: "sk",
    name: "Slovak"
  }, {
    code: "hu",
    name: "Hungarian"
  }, {
    code: "ro",
    name: "Romanian"
  }];
  const advancedFeatures = [{
    title: t("translationsAnalysis.advancedFeatures.legalTerms.title"),
    description: t("translationsAnalysis.advancedFeatures.legalTerms.description"),
    icon: <Badge />
  }, {
    title: t("translationsAnalysis.advancedFeatures.contextual.title"),
    description: t("translationsAnalysis.advancedFeatures.contextual.description"),
    icon: <Glasses />
  }, {
    title: t("translationsAnalysis.advancedFeatures.format.title"),
    description: t("translationsAnalysis.advancedFeatures.format.description"),
    icon: <Laptop />
  }, {
    title: t("translationsAnalysis.advancedFeatures.realtime.title"),
    description: t("translationsAnalysis.advancedFeatures.realtime.description"),
    icon: <Clock />
  }];

  // Get localized language names based on current UI language
  const getLocalizedLanguageName = (languageCode: string, languageName: string): string => {
    // Danish language names when UI is in Danish
    if (i18n.language === 'da') {
      const danishNames: Record<string, string> = {
        'en': 'Engelsk',
        'de': 'Tysk',
        'fr': 'Fransk',
        'es': 'Spansk',
        'it': 'Italiensk',
        'nl': 'Hollandsk',
        'pl': 'Polsk',
        'sv': 'Svensk',
        'da': 'Dansk',
        'fi': 'Finsk',
        'cs': 'Tjekkisk',
        'ro': 'Rumænsk',
        'hu': 'Ungarsk',
        'sk': 'Slovakisk',
        'pt': 'Portugisisk'
      };
      return danishNames[languageCode.toLowerCase()] || languageName;
    }
    return languageName;
  };
  return <FeaturePageLayout title={t("translationsAnalysis.title")} subtitle={t("translationsAnalysis.subtitle")} description={t("translationsAnalysis.description")} icon={<Globe className="h-8 w-8 text-white" />} bgColor="bg-gradient-to-r from-primary/90 via-purple-600/90 to-primary/90">
      {/* Hero Section */}
      <section className="mb-20">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">{t("translationsAnalysis.features.title")}</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-12">
            {t("translationsAnalysis.features.mainDescription")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5
        }} viewport={{
          once: true
        }} className="bg-gray-50 p-8 rounded-lg">
            <div className="mb-6 flex items-center">
              <div className="p-3 bg-primary/10 rounded-full mr-4">
                <Languages className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold">{t("translationsAnalysis.professionalServices.title")}</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              {t("translationsAnalysis.professionalServices.description")}
            </p>
            
            
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5
        }} viewport={{
          once: true
        }} className="bg-gray-50 p-8 rounded-lg">
            <div className="mb-6 flex items-center">
              <div className="p-3 bg-primary/10 rounded-full mr-4">
                <LineChart className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold">{t("translationsAnalysis.tenderAnalysis.title")}</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              {t("translationsAnalysis.tenderAnalysis.description")}
            </p>
            
            
          </motion.div>
        </div>

        
      </section>

      {/* Languages Section */}
      

      {/* Translation Process */}
      

      {/* Advanced Features Section */}
      <section className="mb-20">
        <div className="text-center mb-12">
          
          
        </div>

        
      </section>

      {/* Benefits Section */}
      

      {/* CTA Section */}
      <section>
        
      </section>
    </FeaturePageLayout>;
};
export default TranslationsAnalysis;